import { inject, Injectable } from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { QueryOptions } from 'odata-query';
import { map, Observable } from 'rxjs';
import { GstarClient } from '../../../web-api-client';
import {
  mapExternalUserFromResponse,
  mapExternalUserToCreateRequest,
  mapExternalUserToUpdateRequest,
  mapUserEngagementAssignmentFromResponse,
} from '../mappers';
import { Collection, ExternalUser, UserEngagementAssignment } from '../models';

@Injectable({
  providedIn: 'root',
})
export class ExternalUserService {
  private readonly gstarClient = inject(GstarClient);

  get(
    query?: Partial<QueryOptions<ExternalUser>>
  ): Observable<Collection<ExternalUser>> {
    let orderBy = query?.orderBy?.toString();
    if (orderBy && orderBy.includes('fullName')) {
      orderBy = orderBy.replace('fullName', 'firstName, lastName');
    }

    return this.gstarClient
      .externalusersGET(
        undefined,
        buildQueryFilter(query),
        query?.top?.toString(),
        query?.skip?.toString(),
        orderBy,
        query?.count?.toString(),
        undefined
      )
      .pipe(
        map(response => ({
          total: response.odataCount ?? 0,
          items:
            response.value?.map(response =>
              mapExternalUserFromResponse(response)
            ) ?? [],
        }))
      );
  }

  getById(userId: string): Observable<ExternalUser> {
    return this.gstarClient
      .internalusersGET2(userId)
      .pipe(map(response => mapExternalUserFromResponse(response)));
  }

  getClientAssignments(userId: string): Observable<UserEngagementAssignment[]> {
    return this.gstarClient
      .clientassignmentsGET3(
        userId,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(
        map(response =>
          (response.value ?? []).map(assignment =>
            mapUserEngagementAssignmentFromResponse(assignment)
          )
        )
      );
  }

  create(user: ExternalUser): Observable<ExternalUser> {
    return this.gstarClient
      .externalusersPOST(mapExternalUserToCreateRequest(user))
      .pipe(map(response => mapExternalUserFromResponse(response)));
  }

  update(user: ExternalUser): Observable<void> {
    return this.gstarClient.externalusersPUT(
      user.id,
      mapExternalUserToUpdateRequest(user)
    );
  }

  /**
   * Uses unified `POST .../users/{userId}/deleteuser` (same endpoint as internal users).
   */
  deleteUser(userId: string): Observable<void> {
    return this.gstarClient.deleteuser(userId).pipe(map(() => void 0));
  }
}
